import type { RouteLocationRaw } from 'vue-router'
import { unref } from 'vue'
import type { __SellerLinkFragment } from '../../generated/graphql'

// ----------- Global helpers

export function sleep(ms: number): Promise<void> {
  if (import.meta.server) return Promise.resolve()
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms))
}

export function tryCatch<T>(func: () => T): T | undefined {
  try {
    return func()
  } catch (err) {
    console.trace('tryCatch', err)
    return undefined
  }
}

export function repeat(n: number, cb: (index: number) => void): void {
  for (let index = 0; index < n; index++) {
    cb(index)
  }
}

export function toggleId<T>(oldValue: T | None, newValue: T, getId: (i: T) => string | number): T | undefined {
  return isDef(oldValue) && isDef(newValue) && getId(oldValue) === getId(newValue) ? undefined : newValue
}

export function required<T>(val: T | None, errMsg?: string): T {
  if (isDef(val)) return val
  console.error('[required value not provided]', { val, errMsg })
  console.trace()
  throw new Error('required value not provided: ' + (errMsg || ''))
}

export function getHTMLElement(val: VueComponent | VueComponent[] | None, index = 0): HTMLElement | undefined {
  if (!import.meta.client) return
  let el = unref(Array.isArray(val) ? val[index] : val)
  if (!el) return
  if (el instanceof HTMLElement) return el
  if (el instanceof Element) return el as HTMLElement
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  el = (el as any)?.$el
  el = el instanceof Text ? el?.nextElementSibling : el
  if (el instanceof HTMLElement) return el
  return undefined
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/explicit-module-boundary-types
export function isPromise<T = any>(val: any): val is Promise<T> {
  return isDef(val) && typeof val.then === 'function' && typeof val.catch === 'function'
}

export type ToSellerLink = (args: __SellerLinkFragment | None) => RouteLocationRaw | undefined

export function never<T>(_: never, fallback: () => T): T {
  return fallback()
}

export function log<T>(value: T, ...msg: unknown[]): T {
  const [title, ...rest] = msg
  if (typeof title === 'string') console.log(`   [] ${title}`, value, ...rest)
  else console.log('   [] log', value, ...msg)
  return value
}

// ----------------- Objects

export function isObjectEmpty(value: unknown): boolean {
  if (value == null) return true
  if (typeof value !== 'object') return false

  const proto = Object.getPrototypeOf(value)
  if (proto !== null && proto !== Object.prototype) {
    return false
  }

  for (const prop in value) {
    if (Object.hasOwn(value, prop)) return false
  }
  return true
}

export function isObject(val: unknown): val is Record<string, unknown> {
  return !!val && typeof val === 'object' && !Array.isArray(val)
}

export function toObject<T extends Record<string, unknown>>(obj?: None | unknown | T): T | null {
  return isObject(obj) ? (obj as T) : null
}

// ----------------- Arrays

export function isIn<T extends string | None>(value: T, check1: T, ...check: T[]): boolean {
  return check1 === value || check.includes(value)
}

export function arrify<T>(val: T | T[] | None): T[] {
  return Array.isArray(val) ? val : isDef(val) ? [val] : []
}

export function unArray<T>(val: T[] | T): T | undefined {
  return Array.isArray(val) ? val[0] : val
}

export function arrClean<T>(val: T[] | None): T[] | undefined {
  return val?.length ? val : undefined
}

export function arrFindId<T>(arr: None | T[], id: None | string, cb: (i: T) => string | None): T | undefined {
  if (!arr?.length || !id) return undefined
  return arr.find((i) => cb(i) === id)
}

export function arrFind<T, K>(arr: T[] | None, func: (item: T) => K | None): K | undefined {
  if (!arr?.length) return undefined
  for (const i of arr) {
    const value = func(i)
    if (isDef(value)) return value
  }
  return undefined
}

export function arr<T>(arr: None | T[]): T[]
export function arr<T, K>(arr: None | T[], cb: (value: T, index: number, arr: T[], size: number) => K | None): NN<K>[]
export function arr<T, K = T>(valueArr: unknown | None | T[], cb?: (value: T, index: number, arr: T[], size: number) => K | None): K[] {
  if (!Array.isArray(valueArr) || !valueArr.length) return []
  if (!cb) return valueArr
  const res: K[] = []
  let i
  let index = 0
  for (i of valueArr) {
    const item = cb(i, index, valueArr, res.length)
    if (isDef(item)) res.push(item)
    index += 1
  }
  return res
}

// ----------------- Strings

export function toString2(str: unknown): string | undefined {
  if (typeof str === 'bigint' || typeof str === 'string' || typeof str === 'number') {
    return String(str)
  }
}

export function toString(str: None | string | number | { value?: None | string }): string {
  const s = toString2(str)
  if (s) return s
  if (str && typeof str === 'object' && 'value' in str) return toString2(str.value) || ''
  return ''
}

export function zeroPad(num: string | number, places = 2): string {
  return String(num).padStart(places, '0')
}

export function capitalize(title: string | None): string {
  return typeof title !== 'string' || !title.length ? '' : title.charAt(0).toUpperCase() + title.slice(1)
}

export function isString(val: unknown): val is string {
  return typeof val === 'string'
}

export function onlyString(val: string | None): string {
  return (isString(val) && val) || ''
}

export function joinStringCb<T>(sep: string, arr: T[] | None, cb: (i: T) => string | number | None): string | undefined {
  let str = ''
  arr?.forEach((item) => {
    if (!isDef(item)) return
    const add = cb(item)
    if (typeof add === 'number' || (typeof add === 'string' && add.length > 0)) {
      if (str) str += sep + add
      else str += add
    }
  })

  return str.length ? str : undefined
}

export function joinString(sep: string, ...arr: (string | number | None)[]): string | undefined {
  return joinStringCb(sep, arr, (i) => i)
}

// ----------------- Dates

export function isValidDate(d: Date | None | unknown): d is Date {
  return d instanceof Date && !Number.isNaN(d.getTime())
}

export function toDate(value: unknown): Date | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const d = isValidDate(value) ? value : value ? new Date(value as any) : undefined
  return isValidDate(d) ? d : undefined
}

const dateFormat = {
  monthDay: (d: Date): string => `${zeroPad(d.getMonth() + 1, 2)}.${zeroPad(d.getDate(), 2)}`,
  yearMonthDay: (d: Date): string => `${d.getFullYear()}.${dateFormat.monthDay(d)}`,
  full: (d: Date): string => `${dateFormat.yearMonthDay(d)} ${zeroPad(d.getHours(), 2)}:${zeroPad(d.getMinutes(), 2)}`,
  fullSecods: (d: Date): string => `${dateFormat.full(d)}:${zeroPad(d.getSeconds(), 2)}`,
}

export type UiDateType = 'm:d' | 'y:m:d' | 'y:m:d h:m' | 'y:m:d h:m:s'
export function uiDate(dateInput: Date | string | None | number, options?: UiDateType): string {
  const ui = typeof options === 'string' ? options : undefined
  const date = toDate(dateInput)
  if (!isValidDate(date)) return '-'
  if (ui === 'm:d') return dateFormat.monthDay(date)
  if (ui === 'y:m:d') return dateFormat.yearMonthDay(date)
  if (ui === 'y:m:d h:m:s') return dateFormat.fullSecods(date)
  return dateFormat.full(date)
}

const dateUpdateU = (date: Date, cb: (d: Date) => void) => {
  const d = new Date(date)
  cb(d)
  return d
}
export function dateUpdate(date: Date, type: 'past' | 'future', unit: 'seconds' | 'minute' | 'day' | 'month' | 'year' = 'minute', amount: number): Date {
  const mx = type === 'future' ? 1 : -1
  if (unit === 'year') return dateUpdateU(date, (d) => d.setFullYear(d.getFullYear() + amount * mx))
  if (unit === 'month') return dateUpdateU(date, (d) => d.setMonth(d.getMonth() + amount * mx))
  if (unit === 'day') return dateUpdateU(date, (d) => d.setDate(d.getDate() + amount * mx))

  // [FIXED]: Added .setTime() to actually mutate the date object
  const s = (inc: number) => dateUpdateU(date, (d) => d.setTime(d.getTime() + amount * 1000 * mx * inc))
  if (unit === 'minute') return s(60) // minute
  return s(1) // seconds
}

export function isDef<T>(val: T | null | undefined): val is T {
  return val !== null && val !== undefined
}

type SortCb<T, K> = (i: T) => K | None
type SortDir = 1 | -1

function sorted<T>(inp: T[], cb: (a: T, b: T) => number) {
  return inp.length <= 1 ? inp : [...inp].sort(cb)
}

export function sortSimpleStr<T>(inp: None | T[], cb: SortCb<T, string>, dir: SortDir = 1): T[] {
  return sorted(arrify(inp), (a, b) => (cb(a)?.toString() || '').localeCompare(cb(b)?.toString() || '') * dir)
}

export function sortSimpleNum<T>(inp: None | T[], cb: SortCb<T, number>, dir: SortDir = 1): T[] {
  return sorted(arrify(inp), (a, b) => ((cb(a) ?? Number.MAX_VALUE) - (cb(b) ?? Number.MAX_VALUE)) * dir)
}

export function toSpliced<T>(inp: T[], start: number, deleteCount: number, ...items: T[]): T[] {
  const copy = [...arrify(inp)]
  copy.splice(start, deleteCount, ...items)
  return copy
}

// ----------- Enums

export function toEnum<T extends object>(obj: T, key: string | None): keyof T | undefined {
  return (key && Object.hasOwn(obj, key) && (key as keyof T)) || undefined
}

export function getEnumValue<T extends object>(obj: T, key: string | None): T[keyof T] | undefined {
  const k = toEnum(obj, key)
  return k ? obj[k] : undefined
}

export function getEnumKey<T extends object>(obj: T, key: T[keyof T] | undefined): keyof T | undefined {
  return Object.keys(obj)[Object.values(obj).indexOf(key)] as keyof T
}

// ----------- Numbers

export function isNumber(val: unknown): val is number {
  return typeof val === 'number' && !Number.isNaN(val) && Number.isFinite(val)
}

export function isNumeric(val: unknown): boolean {
  if (isNumber(val)) return true
  if (typeof val === 'string' && val.trim().length > 0) {
    return !Number.isNaN(Number(val.replace(',', '.')))
  }
  return false
}

export function isPositiveNumber(val: unknown): val is number {
  return isNumber(val) && val > 0
}

function parseNum(n: number | string | None | unknown): number | undefined {
  return typeof n === 'number' ? n : typeof n === 'string' ? Number.parseFloat(n.replace(',', '.')) : undefined
}

export function toNumber<T = number>(num: number | string | None | unknown, fallback?: T): NN<number> | T {
  const val = parseNum(num)
  return isNumber(val) ? val : (fallback ?? 0)
}

export function toPositive<T>(num: number | string | None, fallback: T): NN<number> | T {
  const val = parseNum(num)
  return isPositiveNumber(val) ? val : fallback
}

export function fit(value: number | None, min: number, max: number): number {
  return Math.min(max, Math.max(min, value ?? min))
}

export function round(value: number, decimal = 0): number {
  if (decimal === 0) return Math.round(value)
  const mx = Math.pow(10, decimal)
  return Math.round(value * mx) / mx
}

export function add(inp: { decimal: number }, ...nums: number[]): number {
  const base = Math.pow(10, inp.decimal)
  let total = 0
  nums?.forEach((i) => {
    if (isNumber(i)) total += Math.round(i * base)
  })
  return Math.round(total) / base
}

export function toRandom(start: number, end: number): number {
  return round(fit(Math.random() * (end - start) + start, start, end), 0)
}

// ----------- Prices

// [FIX]: Caching the Intl formatter yields a MASSIVE CPU performance boost for large lists
const EUR_FORMATTER =
  typeof Intl !== 'undefined'
    ? new Intl.NumberFormat('de-DE', {
        style: 'currency',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        currency: 'EUR',
      })
    : null

export function priceFormat(num: number): string {
  if (EUR_FORMATTER) return EUR_FORMATTER.format(num)
  return Number.parseFloat(num?.toString() || '0').toFixed(2) + '€'
}

export function priceMx(price: number | None, qty: number | None): number {
  if (!isNumber(price) || !isNumber(qty)) return 0
  return Math.floor(Math.round(price * 100) * qty) / 100
}

export function priceAdd(...nums: number[]): number {
  return add({ decimal: 2 }, ...nums)
}
